import { useRef, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, ImagePlus } from "lucide-react";
import { api, errMsg } from "../../lib/api";

const MAX = 20;
const MAX_MB = 10;
const ALLOWED = ["image/jpeg", "image/png", "image/webp"];

export default function ReportPhotoUploader({ photos, onChange }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (photos.length + files.length > MAX) return toast.error(`Maksimal ${MAX} foto per laporan.`);
    const valid = [];
    for (const f of files) {
      if (!ALLOWED.includes(f.type)) { toast.error(`${f.name}: format harus JPG, PNG, atau WebP.`); continue; }
      if (f.size > MAX_MB * 1024 * 1024) { toast.error(`${f.name}: ukuran melebihi ${MAX_MB}MB.`); continue; }
      valid.push(f);
    }
    if (!valid.length) return;
    setUploading(true);
    const uploaded = [];
    for (const f of valid) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads/report-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.url);
      } catch (err) { toast.error(errMsg(err)); }
    }
    if (uploaded.length) { onChange([...photos, ...uploaded]); toast.success(`${uploaded.length} foto diunggah.`); }
    setUploading(false);
  };

  const remove = (url) => onChange(photos.filter((p) => p !== url));

  return (
    <div data-testid="report-photo-uploader">
      <label className="text-xs font-bold text-slate-600">Foto Inspeksi <span className="text-slate-400 font-medium">({photos.length}/{MAX})</span></label>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 mt-1.5">
        {photos.map((p, i) => (
          <div key={p} className="relative group aspect-square" data-testid={`report-photo-${i}`}>
            <img src={p} alt={`Foto ${i + 1}`} className="w-full h-full object-cover rounded-lg border border-slate-200" />
            <button type="button" onClick={() => remove(p)} data-testid={`remove-photo-${i}`}
              className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity">
              <X size={13} />
            </button>
          </div>
        ))}
        {photos.length < MAX && (
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="add-photo-btn"
            className="aspect-square rounded-lg border-2 border-dashed border-slate-200 hover:border-blue-400 hover:bg-blue-50/50 text-slate-400 hover:text-blue-500 flex flex-col items-center justify-center gap-1 transition-colors disabled:opacity-60">
            {uploading ? <Loader2 size={20} className="animate-spin" /> : <ImagePlus size={20} />}
            <span className="text-[10px] font-bold">{uploading ? "Mengunggah" : "Tambah"}</span>
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={handleFiles} data-testid="photo-file-input" />
      <p className="text-[10px] text-slate-400 mt-1.5">JPG, PNG, atau WebP · maks {MAX_MB}MB per foto · hingga {MAX} foto</p>
    </div>
  );
}
